import {
  Box,
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { useState } from 'react'
import { useSnackbar } from 'stores/useSnackbar'
import { useRedrawWinner } from 'hooks/api/useRedrawWinner'

interface LocationSweepstakesRedrawModalProps {
  isModalOpen: boolean
  onCancel: () => void
  winnerName: string
  sweepstakeDrawingId: number
  sweepstakeWinnerId: number
  redrawCount: number
}

export const LocationSweepstakesRedrawModal = ({
  isModalOpen,
  onCancel,
  winnerName,
  sweepstakeDrawingId,
  sweepstakeWinnerId,
  redrawCount,
}: LocationSweepstakesRedrawModalProps) => {
  const setMessage = useSnackbar((state) => state.setMessage)
  const [reason, setReason] = useState('Select a reason')

  const redrawWinnerMutation = useRedrawWinner({
    onSuccess: () => {
      setMessage('Winner redrawn successfully', 'success')
    },
    onError: () => {
      setMessage('Error redrawing winner', 'error')
    },
  })

  const handleConfirm = () => {
    if (reason.trim()) {
      redrawWinnerMutation.mutate({
        sweepstakeDrawingId,
        redrawReason: reason,
        sweepstakeWinnerId,
      })
    }
  }

  return (
    <Dialog
      open={isModalOpen}
      maxWidth={'xl'}
      fullWidth
      PaperProps={{ sx: { p: 0, maxWidth: '800px' } }}
    >
      <DialogTitle bgcolor={grey[50]} sx={{ p: 0, mb: 2 }} className="border-b">
        <Box px={4} py={4}>
          <Typography variant="h3" fontWeight={'bold'}>
            Redraw Winner
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 4, px: 4 }}>
        <Stack>
          <Stack pb={4}>
            <Typography
              variant="body-1"
              fontWeight={'bold'}
              fontSize={18}
              pb={1}
            >
              Are you sure you want to redraw this winner?
            </Typography>
            <Typography sx={{ color: colors.grey[600] }}>
              {`Selecting an alternative winner involves drawing the next eligible
              name from the standby list. Subsequently, the current winner will
              be deemed "disqualified", rendering them ineligible to receive the
              prize.`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} pb={4}>
            <Typography
              variant="body-1"
              fontWeight={'bold'}
              fontSize={18}
              pt={2}
              sx={{ width: '50%' }}
            >
              Winner Name
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              value={winnerName}
              InputProps={{
                readOnly: true,
              }}
              contentEditable={false}
            />
          </Stack>

          <Stack direction={'row'} spacing={2} pb={4} alignItems={'center'}>
            <Typography
              variant="body-1"
              fontSize={18}
              fontWeight={'bold'}
              sx={{ width: '50%' }}
            >
              Reason for Redraw{' '}
              <Box component="span" sx={{ color: 'red' }}>
                *
              </Box>
            </Typography>
            <FormControl fullWidth>
              <Select
                fullWidth
                labelId="redraw-reason-label"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                <MenuItem value="Select a reason" disabled>
                  Select a reason
                </MenuItem>
                <MenuItem value="Employee">Employee</MenuItem>
                <MenuItem value="Winner didn't claim prize">
                  {`Winner didn't claim prize`}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${grey[300]}`,
          pt: 2,
          pb: 2,
          px: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: '60%' }}
        >
          {`*Each drawing is limited to a maximum of three (3) redraws. To request additional redraws, contact us at rewards@jjgaming.com for assistance. Employees and their immediate family members are not eligible to win. No purchase or play is necessary to enter or win.`}
        </Typography>
        <Box>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={
              !reason.trim() || reason === 'Select a reason' || redrawCount >= 3
            }
          >
            {redrawCount >= 3 ? 'Limit Reached' : 'Redraw Winner'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
